import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export default function LoginPage() {
  const { login, errorMsg } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [localErr, setLocalErr] = useState("");

  // ── Botón "Entrar como admin (dev)" — SOLO en preview ────────────────
  // Doble comprobación por seguridad:
  //   1) Las credenciales tienen que estar en las variables de entorno
  //      del frontend (REACT_APP_DEV_LOGIN_*). En producción NO existen.
  //   2) El hostname debe contener "preview.emergentagent.com". Aunque las
  //      variables se colasen por error a producción, el botón NO se
  //      renderizará porque el hostname será studio.grabarte360.com.
  const isPreviewHost = typeof window !== "undefined"
    && /preview\.emergentagent\.com$/i.test(window.location.hostname);
  const devEmail = process.env.REACT_APP_DEV_LOGIN_EMAIL || "";
  const devPassword = process.env.REACT_APP_DEV_LOGIN_PASSWORD || "";
  const showDevLogin = isPreviewHost && !!devEmail && !!devPassword;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setLocalErr("");
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u?.force_password_change) {
        navigate("/change-password?forced=1", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      // errorMsg ya queda seteado en useAuth.login; lo registramos para debugging
      console.debug("Login failed:", err?.message || err);
    }
    finally { setBusy(false); }
  };

  // Login rápido con credenciales de dev — solo activo en preview.
  const submitDev = async () => {
    setBusy(true); setLocalErr("");
    try {
      const u = await login(devEmail.trim().toLowerCase(), devPassword);
      if (u?.force_password_change) {
        navigate("/change-password?forced=1", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.debug("Dev login failed:", err?.message || err);
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setLocalErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setForgotSent(true);
    } catch (e2) {
      setLocalErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gb-black text-white">
      {/* Muñeco grande de decoración detrás del formulario — el héroe */}
      <img src="/brand/isotype-white.png" alt=""
           className="brand-hero-fade pointer-events-none absolute right-[-8%] top-1/2 h-[820px] w-auto -translate-y-1/2 opacity-[0.07] md:right-[-4%] lg:right-[6%] lg:opacity-[0.1]" />
      <img src="/brand/isotype-white.png" alt=""
           className="brand-hero-fade pointer-events-none absolute -left-32 -bottom-40 h-[420px] w-auto opacity-[0.035]" />

      <header className="relative px-8 py-6">
        <img src="/brand/logo-white.png" alt="grabarte" className="brand-fade-in h-7 w-auto" />
      </header>

      <main className="mx-auto flex max-w-md flex-col items-stretch px-8 pb-16 pt-[6vh] md:max-w-lg">
        <p className="text-[11px] font-semibold uppercase tracking-[0.45em] text-gb-red" data-testid="login-eyebrow">
          grabarte · audiovisual studio
        </p>
        <h1 className="mt-4 gb-display text-4xl leading-[1.05] md:text-5xl" data-testid="login-title">
          {forgotMode ? "Recuperar acceso" : "Iniciar sesión"}
        </h1>
        <p className="mt-4 max-w-md text-base leading-relaxed text-white/65" data-testid="login-sub">
          {forgotMode
            ? "Te enviaremos un email con un enlace para crear una nueva contraseña. Caduca en 1 hora."
            : "Plataforma interna del equipo de Grabarte 360."}
        </p>

        {!forgotMode && (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-white/55" htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" required
                     value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                     className="mt-2 w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-gb-red" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-white/55" htmlFor="password">Contraseña</label>
              <input id="password" type="password" autoComplete="current-password" required
                     value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                     className="mt-2 w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-gb-red" />
            </div>

            {errorMsg && (
              <p className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="login-error">{errorMsg}</p>
            )}

            <button type="submit" disabled={busy || !email || !password} data-testid="login-submit"
                    className="w-full rounded-full bg-gb-red px-5 py-3 text-sm font-semibold uppercase tracking-widest text-white transition hover-gb-red disabled:opacity-40">
              {busy ? "Comprobando…" : "Iniciar sesión"}
            </button>

            <button type="button" onClick={() => { setForgotMode(true); setForgotSent(false); setLocalErr(""); }}
                    data-testid="login-forgot-btn"
                    className="block w-full text-center text-[12px] text-white/60 underline-offset-2 transition hover:text-white hover:underline">
              ¿Has olvidado la contraseña?
            </button>

            {showDevLogin && (
              <div className="mt-4 border-t border-white/10 pt-4">
                <p className="mb-2 text-center text-[10px] uppercase tracking-[0.35em] text-amber-300/70">
                  🛠 Entorno preview
                </p>
                <button type="button" onClick={submitDev} disabled={busy}
                        data-testid="login-dev-btn"
                        className="w-full rounded-full border border-amber-400/50 bg-amber-400/10 px-5 py-2.5 text-[12px] font-semibold uppercase tracking-widest text-amber-200 transition hover:border-amber-300 hover:bg-amber-400/20 disabled:opacity-40">
                  🔓 Entrar como admin (dev)
                </button>
                <p className="mt-2 text-center text-[10px] text-white/40">
                  Solo visible en preview.emergentagent.com
                </p>
              </div>
            )}
          </form>
        )}

        {forgotMode && !forgotSent && (
          <form onSubmit={submitForgot} className="mt-8 space-y-4" data-testid="forgot-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-white/55" htmlFor="femail">Email</label>
              <input id="femail" type="email" required autoFocus
                     value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"
                     className="mt-2 w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-gb-red" />
            </div>
            {localErr && <p className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-200">{localErr}</p>}
            <button type="submit" disabled={busy || !email} data-testid="forgot-submit"
                    className="w-full rounded-full bg-gb-red px-5 py-3 text-sm font-semibold uppercase tracking-widest text-white transition hover-gb-red disabled:opacity-40">
              {busy ? "Enviando…" : "Enviar enlace"}
            </button>
            <button type="button" onClick={() => setForgotMode(false)}
                    className="block w-full text-center text-[12px] text-white/60 transition hover:text-white">
              ← Volver al login
            </button>
          </form>
        )}

        {forgotMode && forgotSent && (
          <div className="mt-8 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100" data-testid="forgot-sent">
            <p className="font-semibold">Email enviado ✓</p>
            <p className="mt-1 text-emerald-100/80">
              Si esa dirección está registrada, recibirás un enlace de restablecimiento. Revisa también la carpeta de spam.
              Si no llega en 5 minutos, contacta con el administrador.
            </p>
            <button onClick={() => { setForgotMode(false); setForgotSent(false); }}
                    className="mt-4 text-xs text-emerald-100 underline hover:text-white">← Volver al login</button>
          </div>
        )}
      </main>

      <footer className="absolute bottom-0 left-0 right-0 px-8 py-5 text-[10px] uppercase tracking-[0.4em] text-white/30">
        © grabarte 360 · audiovisual studio · <Link to="/login" className="hover:text-white/60">acceso interno</Link>
      </footer>
    </div>
  );
}
